/**
 * PortalSidebarIdentityCard — shared top-corner identity for portal shells
 * and organisation Owner / Admin / Member operating shells.
 *
 * Relationship presentation: represented entity, type pill, status
 * (person + capacity live in the sidebar footer).
 * Organisation presentation: company name + workspace status line (no person,
 * no role/type badges).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AppIcon, type IconName } from '@/components/ui/icons';
import Tooltip from '@/components/Tooltip';
import { cn } from '@/lib/utils';
import { SHELL_NAV } from '@/lib/layout/shellNav';
import type {
  PortalSidebarIdentityStatus,
  SidebarIdentityPresentation,
} from '@/lib/layout/resolvePortalSidebarIdentity';

export interface PortalSidebarIdentityCardProps {
  portalLabel: string;
  displayName: string;
  organizationName?: string | null;
  /** Relationship type pill; omit/null for organisation workspace cards. */
  typeLabel?: string | null;
  /** Organisation/workspace prefix before status (e.g. "Organisation account"). */
  workspaceLabel?: string | null;
  status: PortalSidebarIdentityStatus;
  icon: IconName;
  isCollapsed: boolean;
  presentation?: SidebarIdentityPresentation;
  /** Optional route-backed context selector. The whole identity card is the control. */
  switcherHref?: string;
  /** Opens the in-place context switcher when several valid contexts exist. */
  onSwitcherClick?: () => void;
  /** Framer transition for expand/collapse swap */
  transition?: { duration: number; ease: readonly number[] | string };
}

const DEFAULT_TRANSITION = {
  duration: 0.12,
  ease: [0.2, 0, 0, 1] as const,
};

const PortalSidebarIdentityCard: React.FC<PortalSidebarIdentityCardProps> = ({
  portalLabel,
  displayName,
  organizationName,
  typeLabel,
  workspaceLabel,
  status,
  icon,
  isCollapsed,
  presentation = 'relationship',
  switcherHref,
  onSwitcherClick,
  transition = DEFAULT_TRANSITION,
}) => {
  const isOrganization = presentation === 'organization';

  const tooltip = isOrganization
    ? (workspaceLabel ? `${displayName} · ${workspaceLabel}` : displayName)
    : (organizationName ? `${displayName} · ${organizationName}` : displayName);

  return (
    <AnimatePresence mode="wait">
      {!isCollapsed ? (
        <motion.div
          key="portal-identity-expanded"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          <div className="py-3 px-2">
            <div className="px-3 pb-1.5">
              <span className={SHELL_NAV.groupLabel}>{portalLabel}</span>
            </div>
          </div>

          <div className="border-b border-[color:var(--ax1-shell-border)] px-2 pb-3">
            <div className="ax1-shell-identity-card px-2.5 py-2.5">
              {onSwitcherClick ? (
                <button
                  type="button"
                  onClick={onSwitcherClick}
                  className="absolute inset-0 z-[2] rounded-[var(--ax-radius-control)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50"
                  aria-label={`Switch context from ${displayName}`}
                  aria-haspopup="menu"
                >
                  <span className="sr-only">Switch context</span>
                </button>
              ) : switcherHref ? (
                <Link
                  to={switcherHref}
                  className="absolute inset-0 z-[2] rounded-[var(--ax-radius-control)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50"
                  aria-label={`Switch context from ${displayName}`}
                >
                  <span className="sr-only">Switch context</span>
                </Link>
              ) : null}
              <div className="relative z-[1] flex items-center gap-2 mb-1.5">
                <AppIcon
                  name={icon}
                  className="w-3.5 h-3.5 text-status-success-foreground flex-shrink-0"
                />
                <span className="text-[13px] font-semibold text-[color:var(--ax1-shell-text)] truncate">
                  {displayName}
                </span>
              </div>
              {!isOrganization && organizationName && (
                <p className="relative z-[1] text-[11px] text-[color:var(--ax1-shell-muted)] truncate mb-1.5 pl-5">
                  {organizationName}
                </p>
              )}
              <div className="relative z-[1] flex items-center gap-1.5 pl-5">
                {isOrganization && workspaceLabel ? (
                  <>
                    <span className="text-[10px] font-medium text-[color:var(--ax1-shell-muted)] truncate">
                      {workspaceLabel}
                    </span>
                    <span className="text-[color:var(--ax1-shell-muted)]">·</span>
                  </>
                ) : typeLabel ? (
                  <>
                    <span
                      className="ax-badge ax-badge--xs ax-badge--success uppercase tracking-wide"
                      style={{ color: 'var(--ax1-shell-text)' }}
                    >
                      {typeLabel}
                    </span>
                    <span className="text-[color:var(--ax1-shell-muted)]">·</span>
                  </>
                ) : null}
                <div className="flex items-center gap-1">
                  <div className={cn('w-1.5 h-1.5 rounded-full', status.dot)} />
                  <span className={cn('text-[10px] font-medium', status.text)}>
                    {status.label}
                  </span>
                </div>
                {(switcherHref || onSwitcherClick) && (
                  <AppIcon name="chevronDown" className="ml-auto h-3 w-3 text-[color:var(--ax1-shell-muted)]" />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="portal-identity-collapsed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
          className="border-b border-[color:var(--ax1-shell-border)]"
        >
          <Tooltip content={tooltip} position="right">
            {onSwitcherClick ? (
              <button
                type="button"
                onClick={onSwitcherClick}
                className="h-[88px] w-full flex items-center justify-center px-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/50"
                aria-label={`Switch context from ${displayName}`}
                aria-haspopup="menu"
              >
                <div className="ax1-shell-identity-mark w-8 h-8 rounded-[var(--ax-radius-control)] flex items-center justify-center">
                  <AppIcon name={icon} className="w-4 h-4 text-status-success-foreground" />
                </div>
              </button>
            ) : switcherHref ? (
              <Link
                to={switcherHref}
                className="h-[88px] flex items-center justify-center px-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/50"
                aria-label={`Switch context from ${displayName}`}
              >
                <div className="ax1-shell-identity-mark w-8 h-8 rounded-[var(--ax-radius-control)] flex items-center justify-center">
                  <AppIcon name={icon} className="w-4 h-4 text-status-success-foreground" />
                </div>
              </Link>
            ) : (
              <div className="h-[88px] flex items-center justify-center px-1.5">
                <div className="ax1-shell-identity-mark w-8 h-8 rounded-[var(--ax-radius-control)] flex items-center justify-center">
                  <AppIcon name={icon} className="w-4 h-4 text-status-success-foreground" />
                </div>
              </div>
            )}
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PortalSidebarIdentityCard;
